import logger from '../../utils/logger-rum';
/* eslint-disable react-hooks/exhaustive-deps */
import React, {
	memo,
	useContext,
	useEffect,
	useRef,
} from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { GreenroomState as GS } from '../../stores/types/types';
import { VideoStream } from '../types/videos';
import blackImage from '../../images/black-image.jpeg';
import { cleanupVideoStreamElement } from '../../utils/video/video-streams';
import useShotMemos from '../../hooks/use-shot-memos';
import { AVContext } from '../../contexts/audio-video';
import { ROOM_STATES } from '../../stores/reducers/settings';
import Title from './title';
import FeedPresenterSafetyPhoto from './feed-presenter-safety-photo.tsx';

interface Props {
	feedPosition: number;
	mediaId: number;
}

const FeedPresenterSpotlight = (props: Props) => {
	const {
		feedPosition,
		mediaId: grUser,
	} = props;

	const host = useSelector((s: GS) => s.SessionReducer.host);
	const connectedPresenterVideos = useSelector((s: GS) => s.VideoReducer.connectedPresenterVideos);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const embed = useSelector((s: GS) => s.SessionReducer.embed);
	const backstageCanvasVolume = useSelector((s: GS) => s.MeetingReducer.backstageCanvasVolume);
	const roomState = useSelector((s: GS) => s.MeetingReducer.roomState);

	const videoElRef = useRef<HTMLVideoElement>(null);

	const video = connectedPresenterVideos.find(
		(vid: VideoStream) => vid.profile === grUser,
	);

	const { getAV } = useContext(AVContext);
	const { video: hasVideo } = getAV(video?.id);

	const { isOnCanvas, currentShot } = useShotMemos();

	const { shot_id: shotId } = currentShot;

	async function playPresenterFeed(srcObject: MediaStream) {
		if (!videoElRef.current) return;

		videoElRef.current.srcObject = srcObject;
		videoElRef.current.volume = 0;
		if (!srcObject.active) return;

		try {
			await videoElRef.current.play();
			if (videoElRef.current?.paused) {
				try {
					await videoElRef.current.play();
				} catch (e: any) {
					logger.error(
						'video el play request error',
						{ location: 'feed-presenter second' },
						e,
					);
				}
			}
		} catch (e: any) {
			logger.error(
				'video el play request error',
				{ location: 'feed-presenter first' },
				e,
			);
		}

		cleanupVideoStreamElement(videoElRef.current);
	}

	const videoDebounce = useRef<NodeJS.Timeout | null>(null);

	// This attaches connectedPresenterVideo.srcObject to our video DOM element.
	// The srcObject is derived from OpenTok's publisher/subscriber object.
	// This has been known to cause issues where the user's video does not appear.
	// (Presenter appears as a black feed instead.)
	// green-4902
	useEffect(() => {
		if (videoElRef.current && video && video?.srcObject !== videoElRef.current.srcObject) {
			if (videoDebounce.current) { clearTimeout(videoDebounce.current); }
			videoDebounce.current = setTimeout(() => {
				playPresenterFeed(video.srcObject);
			}, 100);
		}
	}, [video?.srcObject, videoElRef.current]);

	useEffect(() => {
		if (videoElRef.current) {
			videoElRef.current.volume = (
				(isOnCanvas || embed || roomState === ROOM_STATES.EDITING)
					? 1
					: backstageCanvasVolume
			);
		}
	}, [isOnCanvas, roomState, backstageCanvasVolume]);

	return (video && video.srcObject && video?.srcObject.active
		? <>
			<video
				key={video.srcObject.id + feedPosition}
				className={classNames(!host && !embed && video.profile === user?.gr_user && 'mirrored')}
				muted={true}
				poster={blackImage}
				autoPlay={true}
				playsInline
				ref={videoElRef}
				onLoadedData={() => {
					// Force play on presenter video elements to avoid a
					// paused OpenTok publisher/subscriber feed.
					// green-4902
					videoElRef.current?.play().catch((e) => {
						logger.error('video el play request error', {
							location: 'feed-presenter third',
						}, e);
					});
				}}
			/>
			<FeedPresenterSafetyPhoto active={!hasVideo} grUserId={grUser} videoId={video.id} />
			<Title key={shotId} idOfPresenter={grUser}/>
		</>
		: <FeedPresenterSafetyPhoto active={true} grUserId={grUser} videoId={video?.id ?? ''} />
	);
};

export default memo(FeedPresenterSpotlight);
